import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapIcon, SearchIcon, TargetIcon, ClipboardCheckIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
const icons = [<MapIcon className="w-6 h-6" data-id="element-792" data-mp="1ku0ejtwy151m" />, <SearchIcon className="w-6 h-6" data-id="element-793" data-mp="1kts2234qg1qy" />, <TargetIcon className="w-6 h-6" data-id="element-794" data-mp="1kwwa5blbjhmw" />, <ClipboardCheckIcon className="w-6 h-6" data-id="element-795" data-mp="1ytja7n8kinx1" />, <UsersIcon className="w-6 h-6" data-id="element-796" data-mp="1yshuh5glxd8z" />];
export function ClassicResearch() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="research" className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900" data-id="element-797" data-mp="23m2lgdjs96yo">
      <div className="absolute inset-0 pointer-events-none" data-id="element-798" data-mp="2ifxt5g87cq8a">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-primary-500/10 blur-3xl hidden sm:block" data-id="element-799" data-mp="2dks6gf6z77j5" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-3xl hidden sm:block" data-id="element-800" data-mp="2pavzk90ti5f7" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-801" data-mp="2y8zl35bsz270">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14 lg:mb-16" data-id="element-802" data-mp="338udxef521up">
          <span className="text-sm font-semibold text-primary-400 tracking-wide uppercase" data-id="element-803" data-mp="3d5gwfwjzsgtk">
            {c.research.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight max-w-4xl mx-auto" data-id="element-804" data-mp="3hzuopq3cy7u0">
            {c.research.title}
          </h2>
          <p className="mt-4 text-lg text-navy-300 max-w-2xl mx-auto leading-relaxed" data-id="element-805" data-mp="3qzzu5uozqw5j">
            {c.research.lead}
          </p>
        </motion.div>

        {/* Pipeline strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.12
      }} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-id="element-806" data-mp="1vvs38rzhesy2">
          <div className="flex items-center px-5 py-3 border-b border-white/10" data-id="element-807" data-mp="20tl7hfh85cvp">
            <span className="text-xs font-semibold tracking-wide uppercase text-navy-400" data-id="element-808" data-mp="2elhu5404iukp">
              Search pipeline
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-y sm:divide-y-0 divide-white/10" data-id="element-809" data-mp="2ajd2ubx5ea18">
            {c.research.funnel.map((f, i) => {
            const isLast = i === c.research.funnel.length - 1;
            return <div key={f.label} className="px-5 py-6 lg:py-7" data-id="element-810" data-mp="u54txk5avz82" data-mpi={i}>
                  <div className={`text-2xl lg:text-3xl font-extrabold tabular-nums ${isLast ? 'text-primary-400' : 'text-white'}`} data-id="element-811" data-mp="u7hk2kl7pfw7">
                    {f.value}
                  </div>
                  <div className="mt-1.5 text-[0.6875rem] tracking-wide uppercase text-navy-400" data-id="element-812" data-mp="35sgqmr5mg5cn">
                    {f.label}
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Steps */}
        <div className="mt-14 lg:mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-id="element-813" data-mp="2i2w3dw2fqlxk">
          {c.research.steps.map((step, i) => <motion.div key={step.title} initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2 + i * 0.08
        }} className="p-6 rounded-2xl bg-white/5 border border-white/10" data-id="element-814" data-mp="2n3a22ni5cjix" data-mpi={i}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-900/30" data-id="element-815" data-mp="6eo6z82m3dwq">
                {icons[i]}
              </div>
              <div className="mt-5 text-xs font-bold tabular-nums text-primary-400" data-id="element-816" data-mp="23esbm8yq6j7a">
                {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="mt-1.5 text-base font-bold text-white" data-id="element-817" data-mp="1yduxq8kr0bbl">
                {step.title}
              </h3>
              <p className="mt-2.5 text-sm text-navy-300 leading-relaxed" data-id="element-818" data-mp="1tifibc73e7d2">
                {step.text}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}